import { useState } from 'react';
import { Monitor, Moon, Sun } from 'lucide-react';
import { useAuth } from '../stores/auth';
import { saveTheme, themeOf, type ThemePref } from '../lib/theme';

const OPTIONS: Array<{ key: ThemePref; label: string; title: string; Icon: typeof Sun }> = [
  { key: 'light', label: 'Light', title: 'White panels, the same colours', Icon: Sun },
  { key: 'dark', label: 'Dark', title: 'Dark panels, easier at night', Icon: Moon },
  { key: 'auto', label: 'Device', title: 'Match my device — follows whatever your computer or phone is set to', Icon: Monitor },
];

/** Light / Dark / Match my device — a compact segmented control for the user menu (prototype §39). */
export function ThemeMenu({ className = '' }: { className?: string }) {
  const user = useAuth((s) => s.user);
  const current = themeOf(user);
  const [error, setError] = useState<string | null>(null);
  const pick = async (pref: ThemePref) => {
    setError(null);
    try { await saveTheme(pref); } catch (e) { setError(e instanceof Error ? e.message : 'Could not save'); }
  };
  return (
    <div className={className}>
      <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-ink-400">Appearance</div>
      <div role="radiogroup" aria-label="Theme" className="grid grid-cols-3 gap-0.5 rounded-lg bg-ink-100 p-0.5">
        {OPTIONS.map(({ key, label, title, Icon }) => (
          <button key={key} type="button" role="radio" aria-checked={current === key} title={title} onClick={() => void pick(key)}
            className={`inline-flex items-center justify-center gap-1.5 rounded-md px-2 py-1 text-[11.5px] font-medium transition ${current === key ? 'bg-white text-ink-900 shadow-card' : 'text-ink-500 hover:text-ink-900'}`}>
            <Icon className="h-3.5 w-3.5" />{label}
          </button>
        ))}
      </div>
      {error && <p className="mt-1 text-[11px] text-red-600">{error}</p>}
    </div>
  );
}
